import React from 'react';
import styled from 'styled-components';
import { CloseOutlined } from '@ant-design/icons';
import { COLORS } from 'theme';

// Styled components for the dialog box
const DialogOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
`;

const DialogContainer = styled.div`
  background-color: #fff;
  border-radius: 12px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
  width: ${props => props.width || '800px'};
  max-width: 95vw;
  max-height: 90vh;
  display: flex;
  flex-direction: column;
  overflow: hidden;
`;

const DialogHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 24px;
  border-bottom: 1px solid #e2e8f0;
`;

const DialogTitle = styled.h2`
  margin: 0;
  font-size: 20px;
  font-weight: 600;
  color: ${COLORS.primary};
`;

const CloseButton = styled.button`
  background: transparent;
  border: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 18px;
  color: #64748b;
  transition: color 0.2s;

  &:hover {
    color: #334155;
  }
`;

const DialogBody = styled.div`
  padding: 24px;
  overflow-y: auto;
  flex: 1;
`;

const DialogFooter = styled.div`
  display: flex;
  justify-content: ${props => props.justifyContent || 'flex-end'};
  padding: 16px 24px;
  border-top: 1px solid #e2e8f0;
  gap: 12px;
`;

// Content section components
const Section = styled.div`
  margin-bottom: 24px;
  padding-bottom: ${props => props.noBorder ? '0' : '24px'};
  border-bottom: ${props => props.noBorder ? 'none' : '1px solid #e2e8f0'};
`;

const SectionTitle = styled.h3`
  font-size: 18px;
  font-weight: 600;
  color: ${COLORS.primary};
  margin-bottom: 16px;
`;

const SectionContent = styled.div`
  color: #334155;
`;

const MetadataItem = styled.div`
  margin-bottom: 12px;
  display: flex;
`;

const MetadataLabel = styled.span`
  font-weight: 600;
  min-width: 150px;
  color: #64748b;
`;

const MetadataValue = styled.span`
  flex: 1;
`;

const QuestionItem = styled.div`
  margin-bottom: 24px;
  padding: 16px;
  background-color: #f8fafc;
  border-radius: 8px;
  border-left: 4px solid ${COLORS.primary};
`;

const QuestionNumber = styled.div`
  font-weight: 600;
  color: ${COLORS.primary};
  margin-bottom: 8px;
`;

const QuestionText = styled.div`
  font-weight: 500;
  margin-bottom: 12px;
`;

const QuestionScenario = styled.div`
  font-style: italic;
  margin-bottom: 12px;
  padding: 12px;
  background-color: #f1f5f9;
  border-radius: 6px;
`;

const OptionsList = styled.ul`
  margin: 12px 0;
  padding-left: 20px;
`;

const OptionItem = styled.li`
  margin-bottom: 8px;
  color: ${props => props.isCorrect ? '#16a34a' : '#334155'};
  font-weight: ${props => props.isCorrect ? '600' : '400'};
`;

const CorrectBadge = styled.span`
  background-color: #dcfce7;
  color: #16a34a;
  padding: 2px 8px;
  border-radius: 4px;
  font-size: 12px;
  margin-left: 8px;
`;

const MetadataList = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 12px;
  font-size: 13px;
  color: #64748b;
`;

const MetadataTag = styled.div`
  background-color: #f1f5f9;
  padding: 4px 8px;
  border-radius: 4px;
  display: flex;
  align-items: center;
`;

const CompetenciesList = styled.ul`
  margin: 8px 0;
  padding-left: 20px;
  color: #64748b;
  font-size: 14px;
`;

const CompetencyItem = styled.li`
  margin-bottom: 4px;
`;

/**
 * DialogBox component for displaying assessment questions
 * 
 * @param {Object} props - Component props
 * @param {boolean} props.visible - Whether the dialog is visible
 * @param {function} props.onClose - Function to call when dialog is closed
 * @param {string} props.title - Dialog title
 * @param {ReactNode} props.children - Dialog content
 * @param {string} props.width - Dialog width (e.g., '800px', '90%')
 * @param {ReactNode} props.footer - Custom footer content
 * @param {string} props.footerAlign - Footer alignment ('flex-start', 'center', 'flex-end')
 */
const DialogBox = ({
  visible,
  onClose,
  title,
  children,
  width,
  footer,
  footerAlign = 'flex-end'
}) => {
  if (!visible) return null;

  return (
    <DialogOverlay onClick={onClose}>
      <DialogContainer width={width} onClick={e => e.stopPropagation()}>
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <CloseButton onClick={onClose}>
            <CloseOutlined />
          </CloseButton>
        </DialogHeader>
        <DialogBody>
          {children}
        </DialogBody>
        {footer && (
          <DialogFooter justifyContent={footerAlign}>
            {footer}
          </DialogFooter>
        )}
      </DialogContainer>
    </DialogOverlay>
  );
};

// Export the main component and all the styled components for use in other files
export {
  DialogBox as default,
  Section,
  SectionTitle,
  SectionContent,
  MetadataItem,
  MetadataLabel,
  MetadataValue,
  QuestionItem,
  QuestionNumber,
  QuestionText,
  QuestionScenario,
  OptionsList,
  OptionItem,
  CorrectBadge,
  MetadataList,
  MetadataTag,
  CompetenciesList,
  CompetencyItem
};
